import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const DOCUMENTS = [
  ['br_number', 'BR Number', 'br_doc', 'br_doc_path'],
  ['assessment_number', 'Assessment Number', 'assessment_doc', 'assessment_image_path'],
  ['tin_number', 'TIN Number', 'tin_doc', 'tin_doc_path'],
  ['epf_no', 'EPF No', 'epf_doc', 'epf_doc_path'],
  ['etf_no', 'ETF No', 'etf_doc', 'etf_doc_path'],
];

function parseJson(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value); } catch (err) { return []; }
}

export default function Personal({ member, shopId, onSaved }) {
  const [form, setForm] = useState({
    member_no:    member?.member_no    || '',
    market_name:  member?.market_name  || '',
    market_name_sinhala: member?.market_name_sinhala || '',
    name:         member?.name         || '',
    br_number:    member?.br_number    || '',
    issues_bonds_by_management_trust: member?.issues_bonds_by_management_trust || '',
    rent_payment_to_trust: member?.rent_payment_to_trust || '',
    assessment_number: member?.assessment_number || '',
    tin_number:   member?.tin_number   || '',
    epf_no:       member?.epf_no       || '',
    etf_no:       member?.etf_no       || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm({
      member_no:    member?.member_no    || '',
      market_name:  member?.market_name  || '',
      market_name_sinhala: member?.market_name_sinhala || '',
      name:         member?.name         || '',
      br_number:    member?.br_number    || '',
      issues_bonds_by_management_trust: member?.issues_bonds_by_management_trust || '',
      rent_payment_to_trust: member?.rent_payment_to_trust || '',
      assessment_number: member?.assessment_number || '',
      tin_number:   member?.tin_number   || '',
      epf_no:       member?.epf_no       || '',
      etf_no:       member?.etf_no       || '',
      previous_owners: member?.previous_owners || '',
    });
  }, [member]);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      if (member?.id) {
        const { data } = await api.put(`/members/${member.id}`, form);
        onSaved(data);
      } else {
        const { data } = await api.post('/members', { shop_id: shopId, ...form });
        onSaved(data);
      }
      toast.success('Personal info saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (field, file) => {
    if (!member?.id) { toast.error('Save basic info first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append(field, file);
      const { data } = await api.post(`/members/${member.id}/upload`, fd);
      onSaved({ ...member, ...data });
      toast.success('Uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const docUrl = value => value ? `https://meegoda.upload.lk/backend/uploads/${value}` : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="grid-3">
        {[
          ['member_no',   'Member No'],
          ['market_name', 'Business Name'],
          ['market_name_sinhala', 'Business Name (Sinhala)'],
          ['name',        'Name of the Member'],
        ].map(([k, label]) => (
          <div className="form-group" key={k}>
            <label className="form-label">{label}</label>
            <input className="form-input" value={form[k]} onChange={set(k)} placeholder={label} />
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 16 }}>Personal Details</h4>
        <div className="grid-3">
          {[
            ['br_number', 'BR Number'],
            ['issues_bonds_by_management_trust', 'Issues Bonds by Management Trust'],
            ['rent_payment_to_trust', 'Rent Payment to Trust'],
            ['assessment_number', 'Assessment Number'],
            ['tin_number', 'TIN Number'],
          ].map(([k, label]) => (
            <div className="form-group" key={k}>
              <label className="form-label">{label}</label>
              <input className="form-input" value={form[k]} onChange={set(k)} placeholder={label} />
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 16 }}>EPF / ETF</h4>
        <div className="grid-2">
          {[
            ['epf_no', 'EPF No'],
            ['etf_no', 'ETF No'],
          ].map(([k, label]) => (
            <div className="form-group" key={k}>
              <label className="form-label">{label}</label>
              <input className="form-input" value={form[k]} onChange={set(k)} placeholder={label} />
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 16 }}>Document Uploads (optional)</h4>
        <div className="grid-3">
          {DOCUMENTS.map(([fieldKey, label, uploadField, pathKey]) => (
            <div className="form-group" key={uploadField}>
              <label className="form-label">{label}</label>
              <input
                className="form-input"
                type="file"
                accept="image/*"
                onChange={e => e.target.files[0] && uploadFile(uploadField, e.target.files[0])}
              />
              {member?.[pathKey] && (
                <a
                  className="doc-link"
                  href={docUrl(member[pathKey])}
                  target="_blank"
                  rel="noreferrer"
                  style={{ marginTop: 8, display: 'inline-block', fontSize: 13 }}
                >
                  View {label}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginBottom: 16 }}>Owner Images</h4>
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Owner Image</label>
            <input
              className="form-input"
              type="file"
              accept="image/*"
              onChange={e => e.target.files[0] && uploadFile('owner_image', e.target.files[0])}
            />
            {member?.owner_image_path && (
              <a
                className="doc-link"
                href={docUrl(member.owner_image_path)}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: 8, display: 'inline-block', fontSize: 13 }}
              >
                View Image
              </a>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Owner Signature</label>
            <input
              className="form-input"
              type="file"
              accept="image/*"
              onChange={e => e.target.files[0] && uploadFile('owner_signature', e.target.files[0])}
            />
            {member?.owner_signature_path && (
              <a
                className="doc-link"
                href={docUrl(member.owner_signature_path)}
                target="_blank"
                rel="noreferrer"
                style={{ marginTop: 8, display: 'inline-block', fontSize: 13 }}
              >
                View Signature
              </a>
            )}
          </div>
        </div>
      </div>

      {uploading && <p style={{ color: 'var(--primary)', fontSize: 13 }}>Uploading…</p>}

      <div className="section-save-bar">
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Personal Info'}
        </button>
      </div>
    </div>
  );
}
